import React, { useEffect, useState } from "react";
import { Clock, Coins, Receipt, Cube } from "@phosphor-icons/react";
import { Badge } from "@/components/primitives";
import { PoolStatRow } from "@/components/panels/PoolSummaryPanel";
import { formatCountdown, formatDuration, formatLovelace, truncMid, unixToDate } from "@/lib/format";

const EpochBlockRef = ({ label, block, onNavigateBlock }) => (
  <PoolStatRow
    icon={Cube}
    label={label}
    value={
      block && block.block_height != null ? (
        <button
          type="button"
          data-testid={`epoch-${label.toLowerCase().replace(/\s+/g, "-")}`}
          onClick={() => onNavigateBlock?.(block.block_height)}
          className="text-primary hover:text-[#22d3ee] transition-colors underline decoration-primary/30 underline-offset-4"
        >
          #{Number(block.block_height).toLocaleString("en-US")}
        </button>
      ) : (
        "—"
      )
    }
    sub={block && block.hash ? truncMid(block.hash, 10, 8) : null}
  />
);

// ---------- Epochs mode summary panel ----------
export const EpochSummaryPanel = ({ data, amountMode, onNavigateBlock }) => {
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);
  if (!data) return null;
  const nowSec = Math.floor(Date.now() / 1000);
  const start = Number(data.start_time) || 0;
  const dbEnd = Number(data.end_time) || 0;
  // Preprod epochs are 5 days (432000s). For the ongoing epoch the DB `end_time`
  // only reflects the latest block, so derive the scheduled boundary from start.
  const EPOCH_LENGTH_SEC = 432000;
  const scheduledEnd = start + EPOCH_LENGTH_SEC;
  const isActive = nowSec >= start && nowSec < scheduledEnd;
  const end = isActive ? scheduledEnd : dbEnd;
  const total = Math.max(1, end - start);
  const elapsed = Math.min(total, Math.max(0, nowSec - start));
  const pct = Math.min(100, Math.round((elapsed / total) * 100));

  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-primary/25 bg-card/30 backdrop-blur-md box-glow px-5 py-3"
      data-testid="epoch-summary-panel"
    >
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-primary to-transparent opacity-70 tracing-beam pointer-events-none" />
      <div className="pt-4 pb-1 flex items-center gap-3 flex-wrap" data-testid="epoch-header">
        <span className="font-heading text-[22px] text-primary text-glow">Epoch {data.epoch}</span>
        {isActive ? (
          <Badge tone="success">in progress</Badge>
        ) : (
          <Badge tone="info">finished</Badge>
        )}
      </div>

      {isActive ? (
        <div className="py-3 border-b border-primary/10" data-testid="epoch-progress">
          <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.2em] text-muted-foreground mono mb-2">
            <span>{pct}% elapsed</span>
            <span>ends in {formatCountdown(end)}</span>
          </div>
          <div className="h-2 rounded-full bg-primary/10 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-primary to-[#22d3ee] transition-all duration-700"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
      ) : null}

      <PoolStatRow icon={Clock} label="Start Time" value={unixToDate(data.start_time)} />
      <PoolStatRow
        icon={Clock}
        label="End Time"
        value={unixToDate(end)}
        sub={isActive ? "scheduled (estimated)" : null}
      />
      <PoolStatRow
        icon={Clock}
        label="Duration"
        value={formatDuration(total)}
        sub={isActive ? `${formatCountdown(end)} remaining` : null}
      />
      <PoolStatRow icon={Coins} label="Total Output" big value={formatLovelace(data.output, amountMode)} />
      <PoolStatRow icon={Coins} label="Total Fees" value={formatLovelace(data.fees, amountMode)} />
      <PoolStatRow
        icon={Receipt}
        label="Transactions"
        value={(data.tx_count ?? 0).toLocaleString("en-US")}
      />
      <PoolStatRow
        icon={Cube}
        label="Blocks"
        value={(data.block_count ?? 0).toLocaleString("en-US")}
      />
      <EpochBlockRef label="First Block" block={data.first_block} onNavigateBlock={onNavigateBlock} />
      <EpochBlockRef label="Last Block" block={data.last_block} onNavigateBlock={onNavigateBlock} />
    </div>
  );
};
